/**
 * Utility functions for processing and formatting email analyzer recommendations
 */

/**
 * Processes instances to extract and deduplicate terms for recommendation display
 * @param instances Array of instances with term, location and count properties
 * @return Processed term data for recommendations
 */
export function processTermsForRecommendation(instances: any[]): {
  uniqueTerms: string[];
  termCounts: Record<string, number>;
  totalCount: number;
  termsText: string;
} {
  // Filter out instances without terms
  const validInstances = instances.filter(instance => instance.term);
  
  // Extract unique terms
  const uniqueTerms = Array.from(new Set(
    validInstances.map(instance => instance.term)
  ));
  
  // Count total instances and per-term counts
  const termCounts: Record<string, number> = {};
  let totalCount = 0;
  
  validInstances.forEach(instance => {
    const count = instance.count || 1;
    termCounts[instance.term] = (termCounts[instance.term] || 0) + count;
    totalCount += count;
  });
  
  // Create formatted text of unique terms
  const termsText = uniqueTerms
    .map(term => `'${term}'`)
    .join(', ');
  
  return {
    uniqueTerms,
    termCounts,
    totalCount,
    termsText
  };
}

/**
 * Creates a formatted string with instance details for recommendation display
 * @param instances Array of instances to format
 * @return Formatted string with instance details
 */
export function formatInstanceDetails(instances: any[]): string {
  if (!instances || instances.length === 0) {
    return '';
  }
  
  return instances
    .filter(instance => instance.term || instance.details)
    .map(instance => 
      `• ${instance.term ? `'${instance.term}'` : ''} ${instance.details ? `(${instance.details})` : ''} in ${instance.location || 'body'}`
    )
    .join('\n');
}

/**
 * Formats a list of terms with their counts for display
 * @param termCounts Record mapping terms to their counts
 * @return Formatted string with terms and their counts
 */
export function formatTermsWithCounts(termCounts: Record<string, number>): string {
  return Object.entries(termCounts)
    .map(([term, count]) => `'${term}' (${count}x)`)
    .join(', ');
}

/**
 * Processes a recommendation template with term data
 * @param template The recommendation template text
 * @param termData Processed term data from processTermsForRecommendation
 * @param defaultTermDescription Fallback term description if no terms found
 */
export function processTemplate(
  template: string,
  termData: ReturnType<typeof processTermsForRecommendation>,
  defaultTermDescription: string = 'trigger words'
): string {
  const { uniqueTerms, termCounts, totalCount, termsText } = termData;
  
  let processed = template;
  
  // Replace {term} with appropriate text based on available terms
  if (uniqueTerms.length === 1) {
    processed = processed.replace(/\{term\}/g, uniqueTerms[0]);
  } else if (uniqueTerms.length > 0) {
    processed = processed.replace(/\{term\}/g, `these terms (${termsText})`);
  } else {
    processed = processed.replace(/\{term\}/g, defaultTermDescription);
  }
  
  // Replace {count} with total count
  processed = processed.replace(/\{count\}/g, totalCount.toString());
  
  // Handle {instanceDetails} replacement
  const instanceDetails = uniqueTerms.map(term => 
    `• '${term}' found ${termCounts[term]} time${termCounts[term] > 1 ? 's' : ''}`
  ).join('\n');
  
  processed = processed.replace(/\{instanceDetails\}/g, instanceDetails);
  
  return processed;
} 